import React, { useEffect } from 'react'
import {
  Checkbox,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Select,
  TimePicker,
} from 'antd'
import { ArrowRightOutlined } from '@ant-design/icons'
import { useUnit } from 'effector-react'
import { Dayjs } from 'dayjs'
import { NumericInput } from '../NumericInput'
import { decimalValidator } from '../../../../../libs/utils'
import { $walletAllowedList } from '../GeneralTransactionModal/model'
import { products } from '../../../../../libs/products'
import { ReasonAutoComplete } from '../../../../../shared/reasonAutocomplete'

export const useDefaultCurrencyOnce = (
  form: FormInstance<any>,
  sourceName: string,
  targetName: string,
) => {
  const sourceValue = Form.useWatch(sourceName, form)
  useEffect(() => {
    if (sourceValue && !form.getFieldValue(targetName)) {
      form.setFieldValue(targetName, sourceValue)
    }
  }, [sourceValue, form, targetName])
}

type CurrencyOption = string

type WalletSelectProps = {
  name: string
  label: string
  required?: boolean
  disabled?: boolean
  style?: React.CSSProperties
}

export const WalletSelect: React.FC<WalletSelectProps> = ({
  name,
  label,
  required,
  disabled,
  style,
}) => {
  const wallets = useUnit($walletAllowedList)
  return (
    <Form.Item
      name={name}
      label={label}
      rules={required ? [{ required: true, message: `${label} is required` }] : []}
      style={style}
    >
      <Select
        placeholder="Select wallet"
        disabled={disabled}
        showSearch
        optionFilterProp="label"
        filterSort={(a, b) =>
          String(a.label ?? '').localeCompare(String(b.label ?? ''))
        }
        options={wallets.map((w) => ({ value: String(w.id), label: w.name }))}
      />
    </Form.Item>
  )
}

type LockedTypeSelectProps = {
  label?: string
  value: string
  options: Array<{ value: string; text: string }>
  style?: React.CSSProperties
}

export const LockedTypeSelect: React.FC<LockedTypeSelectProps> = ({
  label = 'Type',
  value,
  options,
  style,
}) => (
  <Form.Item label={label} style={style}>
    <Select
      value={value}
      disabled
      options={options.map((o) => ({ value: o.value, label: o.text }))}
    />
  </Form.Item>
)

type OperationTypeSelectProps = {
  name: string
  label?: string
  required?: boolean
  disabled?: boolean
  options: Array<{ value: string; text: string }>
  style?: React.CSSProperties
}

export const OperationTypeSelect: React.FC<OperationTypeSelectProps> = ({
  name,
  label = 'Type of transaction',
  required,
  disabled,
  options,
  style,
}) => (
  <Form.Item
    name={name}
    label={label}
    rules={required ? [{ required: true, message: 'Type is required' }] : []}
    style={style}
  >
    <Select
      placeholder="Select type"
      disabled={disabled}
      options={options.map((o) => ({ value: o.value, label: o.text }))}
    />
  </Form.Item>
)

type ProductSelectProps = {
  name: string
  label?: string
  required?: boolean
  allowClear?: boolean
  style?: React.CSSProperties
}

export const ProductSelect: React.FC<ProductSelectProps> = ({
  name,
  label = 'Product',
  required,
  allowClear = true,
  style,
}) => (
  <Form.Item
    name={name}
    label={label}
    rules={required ? [{ required: true, message: 'Product is required' }] : []}
    style={style}
  >
    <Select
      placeholder="Select product"
      allowClear={allowClear}
      options={products.map((p: { value: string; text: string }) => ({
        value: p.value,
        label: p.text,
      }))}
    />
  </Form.Item>
)

type TextFieldProps = {
  name: string
  label: string
  placeholder?: string
  required?: boolean
  historyScope?: string
  style?: React.CSSProperties
}

const NO_AUTOFILL_PROPS = {
  autoComplete: 'off',
  'data-lpignore': 'true',
  'data-form-type': 'other',
  'data-1p-ignore': true,
}

const ReasonAutoCompleteAdapter: React.FC<{
  scope: string
  placeholder?: string
  value?: string
  onChange?: (val: string) => void
}> = ({ scope, placeholder, value, onChange }) => (
  <ReasonAutoComplete
    scope={scope}
    placeholder={placeholder}
    value={value ?? ''}
    onChange={(val) => onChange?.(val)}
  />
)

export const TextField: React.FC<TextFieldProps> = ({
  name,
  label,
  placeholder,
  required,
  historyScope,
  style,
}) => (
  <Form.Item
    name={name}
    label={label}
    rules={required ? [{ required: true, message: `${label} is required` }] : []}
    style={style}
  >
    {historyScope ? (
      <ReasonAutoCompleteAdapter scope={historyScope} placeholder={placeholder} />
    ) : (
      <Input placeholder={placeholder} {...NO_AUTOFILL_PROPS} />
    )}
  </Form.Item>
)

type AmountWithCurrencyProps = {
  amountName: string
  currencyName: string
  amountLabel: string
  currencies: CurrencyOption[]
  required?: boolean
  allowNegative?: boolean
  currencyRequired?: boolean
  currencyAllowClear?: boolean
  amountFlex?: number
  currencyWidth?: number
  style?: React.CSSProperties
}

export const AmountWithCurrency: React.FC<AmountWithCurrencyProps> = ({
  amountName,
  currencyName,
  amountLabel,
  currencies,
  required,
  allowNegative,
  currencyRequired,
  currencyAllowClear,
  amountFlex = 1,
  currencyWidth = 120,
  style,
}) => {
  const amountRules = [
    ...(required
      ? [{ required: true, message: `${amountLabel} is required` }]
      : []),
    {
      validator: decimalValidator({
        allowEmpty: !required,
        allowNegative: !!allowNegative,
      }),
    },
  ]
  return (
    <div style={{ display: 'flex', gap: 8, ...style }}>
      <Form.Item
        name={amountName}
        label={amountLabel}
        rules={amountRules}
        style={{ flex: amountFlex }}
      >
        <NumericInput placeholder="0" />
      </Form.Item>
      <Form.Item
        name={currencyName}
        label=" "
        rules={
          currencyRequired
            ? [{ required: true, message: 'Currency is required' }]
            : []
        }
        style={{ width: currencyWidth }}
      >
        <Select
          placeholder="Currency"
          allowClear={currencyAllowClear}
          options={currencies.map((c) => ({ value: c, label: c }))}
        />
      </Form.Item>
    </div>
  )
}

type AmountSentReceivedRowProps = {
  sentAmountName: string
  sentCurrencyName: string
  sentLabel?: string
  sentCurrencies: CurrencyOption[]
  sentRequired?: boolean
  sentAllowNegative?: boolean
  sentCurrencyRequired?: boolean
  sentCurrencyAllowClear?: boolean
  receivedAmountName: string
  receivedCurrencyName: string
  receivedLabel?: string
  receivedCurrencies: CurrencyOption[]
  receivedRequired?: boolean
  receivedAllowNegative?: boolean
  receivedCurrencyRequired?: boolean
  receivedCurrencyAllowClear?: boolean
}

export const AmountSentReceivedRow: React.FC<AmountSentReceivedRowProps> = ({
  sentAmountName,
  sentCurrencyName,
  sentLabel = 'Sent',
  sentCurrencies,
  sentRequired,
  sentAllowNegative,
  sentCurrencyRequired,
  sentCurrencyAllowClear,
  receivedAmountName,
  receivedCurrencyName,
  receivedLabel = 'Received',
  receivedCurrencies,
  receivedRequired,
  receivedAllowNegative,
  receivedCurrencyRequired,
  receivedCurrencyAllowClear,
}) => (
  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
    <AmountWithCurrency
      amountName={sentAmountName}
      currencyName={sentCurrencyName}
      amountLabel={sentLabel}
      currencies={sentCurrencies}
      required={sentRequired}
      allowNegative={sentAllowNegative}
      currencyRequired={sentCurrencyRequired}
      currencyAllowClear={sentCurrencyAllowClear}
      style={{ flex: 1 }}
    />
    <ArrowRightOutlined style={{ marginBottom: 32, fontSize: 18 }} />
    <AmountWithCurrency
      amountName={receivedAmountName}
      currencyName={receivedCurrencyName}
      amountLabel={receivedLabel}
      currencies={receivedCurrencies}
      required={receivedRequired}
      allowNegative={receivedAllowNegative}
      currencyRequired={receivedCurrencyRequired}
      currencyAllowClear={receivedCurrencyAllowClear}
      style={{ flex: 1 }}
    />
  </div>
)

type CommissionRowProps = {
  sentName: string
  sentCurrencyName: string
  sentCurrencies: CurrencyOption[]
  receivedName: string
  receivedCurrencyName: string
  receivedCurrencies: CurrencyOption[]
}

export const CommissionRow: React.FC<CommissionRowProps> = ({
  sentName,
  sentCurrencyName,
  sentCurrencies,
  receivedName,
  receivedCurrencyName,
  receivedCurrencies,
}) => (
  <div style={{ display: 'flex', gap: 12 }}>
    <AmountWithCurrency
      amountName={sentName}
      currencyName={sentCurrencyName}
      amountLabel="Sent commission"
      currencies={sentCurrencies}
      allowNegative
      currencyAllowClear
      style={{ flex: 1 }}
    />
    <AmountWithCurrency
      amountName={receivedName}
      currencyName={receivedCurrencyName}
      amountLabel="Received commission"
      currencies={receivedCurrencies}
      allowNegative
      currencyAllowClear
      style={{ flex: 1 }}
    />
  </div>
)

type ExchangeRateFieldProps = {
  name: string
  label?: string
  style?: React.CSSProperties
}

export const ExchangeRateField: React.FC<ExchangeRateFieldProps> = ({
  name,
  label = 'Exchanger rate',
  style,
}) => (
  <Form.Item
    name={name}
    label={label}
    rules={[{ validator: decimalValidator() }]}
    style={style}
  >
    <NumericInput placeholder="0" />
  </Form.Item>
)

type ExchangeRateRowProps = {
  sentName: string
  sentLabel?: string
  receivedName: string
  receivedLabel?: string
}

export const ExchangeRateRow: React.FC<ExchangeRateRowProps> = ({
  sentName,
  sentLabel = 'Sending exchanger rate',
  receivedName,
  receivedLabel = 'Acceptance exchanger rate',
}) => (
  <div style={{ display: 'flex', gap: 12 }}>
    <ExchangeRateField name={sentName} label={sentLabel} style={{ flex: 1 }} />
    <ExchangeRateField
      name={receivedName}
      label={receivedLabel}
      style={{ flex: 1 }}
    />
  </div>
)

type DateTimeRowProps = {
  dateName?: string
  timeName?: string
  inProgressName?: string
}

export const DateTimeRow: React.FC<DateTimeRowProps> = ({
  dateName = 'date',
  timeName = 'time',
  inProgressName,
}) => (
  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
    <Form.Item
      name={dateName}
      label="Date"
      rules={[{ required: true, message: 'Date is required' }]}
      style={{ flex: 1 }}
    >
      <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
    </Form.Item>
    <Form.Item
      name={timeName}
      label="Time"
      rules={[{ required: true, message: 'Time is required' }]}
      style={{ flex: 1 }}
    >
      <TimePicker style={{ width: '100%' }} format="HH:mm:ss" />
    </Form.Item>
    {inProgressName && (
      <Form.Item
        name={inProgressName}
        valuePropName="checked"
        style={{ marginBottom: 18 }}
      >
        <Checkbox>In progress</Checkbox>
      </Form.Item>
    )}
  </div>
)

type CommentFieldProps = {
  name?: string
  label?: string
  required?: boolean
  rows?: number
}

export const CommentField: React.FC<CommentFieldProps> = ({
  name = 'comment',
  label = 'Comment',
  required,
  rows = 2,
}) => (
  <Form.Item
    name={name}
    label={label}
    rules={required ? [{ required: true, message: 'Comment is required' }] : []}
  >
    <Input.TextArea rows={rows} />
  </Form.Item>
)

export type DayjsValue = Dayjs | null
